type StreamErrorCategory =
    | 'network_stream_failure'
    | 'stream_idle_timeout'
    | 'validation_error'
    | 'rate_limited'
    | 'unknown_stream_error';

const NETWORK_ERROR_PATTERNS = [
    'load failed',
    'loading failed',
    'failed to fetch',
    'networkerror',
    'err_network',
    'err_internet_disconnected',
    'connection error',
    'connection reset',
    'connection refused',
    'econnrefused',
    'chrome network request timeout',
    'network timeout',
];

const getErrorMessage = (error: unknown): string => {
    if (typeof error === 'string') {
        return error;
    }

    if (error instanceof Error) {
        return error.message;
    }

    if (error === null || error === undefined) {
        return 'Unknown streaming error';
    }

    if (typeof error === 'object' && 'message' in error) {
        const msg = (error as { message: unknown }).message;
        if (typeof msg === 'string') return msg;
    }

    try {
        const json = JSON.stringify(error);
        if (json !== '{}') return json;
    } catch {
        // circular reference or other serialization issue
    }

    return String(error);
};

export const classifyStreamingError = (
    error: unknown
): {
    category: StreamErrorCategory;
    message: string;
    isRetryable: boolean;
} => {
    const message = getErrorMessage(error);
    const normalized = message.toLowerCase();

    const isIdleTimeout =
        normalized.includes('idle timeout') ||
        normalized.includes('stopped responding') ||
        normalized.includes('request timeout') ||
        normalized.includes('took too long');
    if (isIdleTimeout) {
        return {
            category: 'stream_idle_timeout',
            message,
            isRetryable: false,
        };
    }

    const isRateLimited =
        normalized.includes('rate_limited') ||
        normalized.includes('already in progress for this session');
    if (isRateLimited) {
        return {
            category: 'rate_limited',
            message,
            isRetryable: true,
        };
    }

    const isValidationError =
        normalized.includes('"detail"') &&
        (normalized.includes('"loc"') || normalized.includes('"type"'));
    if (isValidationError) {
        return {
            category: 'validation_error',
            message,
            isRetryable: false,
        };
    }

    const isNetworkFailure = NETWORK_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern));
    if (isNetworkFailure) {
        return {
            category: 'network_stream_failure',
            message,
            isRetryable: true,
        };
    }

    return {
        category: 'unknown_stream_error',
        message,
        isRetryable: false,
    };
};
